import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import type { InvestmentPack } from "../../types/api";
import type {
  CreatePackPayload,
  UpdatePackPayload,
} from "../../services/investmentPackService";
import { resolveAssetUrl } from "../../utils/url";
import { useTranslation } from "react-i18next";
import { X, Plus } from "lucide-react";

type PackFormModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    payload: CreatePackPayload | UpdatePackPayload,
    isEdit: boolean
  ) => Promise<void>;
  isSubmitting: boolean;
  pack?: InvestmentPack | null;
};

const defaultForm: CreatePackPayload = {
  name: "",
  description: null,
  totalAmount: 1000,
  maxPurchases: null,
  primaryColor: null,
  secondaryColor: null,
  benefits: [],
};

interface BenefitRow {
  benefitText: string;
  displayOrder: number;
}

const PackFormModal = ({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  pack,
}: PackFormModalProps) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<CreatePackPayload>({ ...defaultForm });
  const [benefits, setBenefits] = useState<BenefitRow[]>([]);

  useEffect(() => {
    if (pack) {
      setForm({
        name: pack.name,
        description: pack.description ?? null,
        totalAmount: pack.totalAmount,
        maxPurchases: pack.maxPurchases ?? null,
        primaryColor: pack.primaryColor ?? null,
        secondaryColor: pack.secondaryColor ?? null,
        benefits:
          pack.benefits?.map((b) => ({
            benefitText: b.benefitText,
            displayOrder: b.displayOrder,
          })) ?? [],
      });
      setBenefits(
        pack.benefits?.map((b) => ({
          benefitText: b.benefitText,
          displayOrder: b.displayOrder,
        })) ?? []
      );
    } else {
      setForm({ ...defaultForm });
      setBenefits([]);
    }
  }, [pack]);

  const handleAddBenefit = () => {
    setBenefits([
      ...benefits,
      { benefitText: "", displayOrder: benefits.length },
    ]);
  };

  const handleRemoveBenefit = (index: number) => {
    setBenefits(benefits.filter((_, i) => i !== index));
  };

  const handleBenefitChange = (
    index: number,
    field: "benefitText",
    value: string
  ) => {
    setBenefits(
      benefits.map((b, i) => (i === index ? { ...b, [field]: value } : b))
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Les avantages sont optionnels
    const payload: CreatePackPayload | UpdatePackPayload = {
      name: form.name,
      description: form.description ?? null,
      totalAmount: form.totalAmount,
      maxPurchases: form.maxPurchases ?? null,
      primaryColor: form.primaryColor ?? null,
      secondaryColor: form.secondaryColor ?? null,
      benefits:
        benefits.length > 0
          ? benefits.map((b, i) => ({
              benefitText: b.benefitText,
              displayOrder: b.displayOrder ?? i,
            }))
          : [],
    };

    try {
      await onSubmit(payload, Boolean(pack));
      onClose();
    } catch {
      // Les erreurs sont gérées par le parent
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-4xl overflow-auto bg-slate-950/95 text-white p-0">
        <div className="p-6">
          <DialogHeader className="mb-6">
            <DialogTitle className="text-3xl font-bold text-white">
              {pack
                ? t("admin.packs.form.editTitle")
                : t("admin.packs.form.createTitle")}
            </DialogTitle>
            <p className="text-sm text-white/70 mt-2">
              {t("admin.packs.subtitle")}
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm p-6 shadow-lg">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.packs.form.nameLabel")}</Label>
                  <Input
                    value={form.name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label>{t("admin.packs.form.descriptionLabel")}</Label>
                  <Textarea
                    value={form.description ?? ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        description: event.target.value || null,
                      }))
                    }
                    className="bg-white/5 text-white border-white/10"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm p-6 shadow-lg">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.packs.form.totalAmountLabel")}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.totalAmount}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        totalAmount: Number.parseFloat(event.target.value) || 0,
                      }))
                    }
                    required
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.packs.form.maxPurchasesLabel")}</Label>
                  <Input
                    type="number"
                    step="1"
                    min="1"
                    value={form.maxPurchases ?? ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        maxPurchases: event.target.value
                          ? Number.parseInt(event.target.value, 10)
                          : null,
                      }))
                    }
                    placeholder={t("admin.packs.form.maxPurchasesPlaceholder")}
                    className="bg-white/5 text-white border-white/10"
                  />
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/50 to-slate-800/30 backdrop-blur-sm p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t("admin.packs.form.colorsLabel")}
              </h3>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label>{t("admin.packs.form.primaryColorLabel")}</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.primaryColor || "#6366f1"}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          primaryColor: event.target.value,
                        }))
                      }
                      className="h-12 w-20 rounded-lg border border-white/10 bg-white/5 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={form.primaryColor || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          primaryColor: event.target.value || null,
                        }))
                      }
                      placeholder="#6366f1"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1 bg-white/5 text-white border-white/10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>{t("admin.packs.form.secondaryColorLabel")}</Label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.secondaryColor || "#8b5cf6"}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          secondaryColor: event.target.value,
                        }))
                      }
                      className="h-12 w-20 rounded-lg border border-white/10 bg-white/5 cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={form.secondaryColor || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          secondaryColor: event.target.value || null,
                        }))
                      }
                      placeholder="#8b5cf6"
                      pattern="^#[0-9A-Fa-f]{6}$"
                      className="flex-1 bg-white/5 text-white border-white/10"
                    />
                  </div>
                </div>
              </div>
              {(form.primaryColor || form.secondaryColor) && (
                <div className="mt-4 p-4 rounded-lg border border-white/10 bg-white/5">
                  <p className="text-xs text-white/60 mb-2">
                    {t("admin.packs.form.colorPreview")}
                  </p>
                  <div
                    className="h-20 rounded-lg"
                    style={{
                      background:
                        form.primaryColor && form.secondaryColor
                          ? `linear-gradient(135deg, ${form.primaryColor} 0%, ${form.secondaryColor} 100%)`
                          : form.primaryColor ||
                            form.secondaryColor ||
                            "transparent",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="space-y-4 border-t border-white/10 pt-4">
              <div className="flex items-center justify-between">
                <Label>
                  {t("admin.packs.form.benefitsLabel")}
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleAddBenefit}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t("admin.packs.form.addBenefit")}
                </Button>
              </div>

              {benefits.length === 0 ? (
                <p className="text-sm text-white/60">
                  {t(
                    "admin.packs.form.noBenefitsMessage"
                  )}
                </p>
              ) : (
                <div className="space-y-2">
                  {benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-start gap-2 rounded-lg border border-white/10 bg-white/5 p-3">
                      <div className="flex-1 space-y-2">
                        <Input
                          placeholder={t(
                            "admin.packs.form.benefitTextPlaceholder"
                          )}
                          value={benefit.benefitText}
                          onChange={(e) =>
                            handleBenefitChange(
                              index,
                              "benefitText",
                              e.target.value
                            )
                          }
                          className="bg-white/5 text-white border-white/10"
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveBenefit(index)}
                        className="text-red-400 hover:text-red-300">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="ghost"
                className="text-white"
                onClick={onClose}
                disabled={isSubmitting}>
                {t("admin.packs.form.cancelButton")}
              </Button>
              <Button
                type="submit"
                className="min-w-[180px] rounded-2xl bg-gradient-to-r from-cyan-400 to-blue-500 text-base font-semibold shadow-lg shadow-cyan-500/30 transition hover:translate-y-[1px] hover:shadow-cyan-500/40"
                disabled={isSubmitting}>
                {isSubmitting
                  ? t("admin.packs.form.savingButton")
                  : pack
                  ? t("admin.packs.form.updateButton")
                  : t("admin.packs.form.createButton")}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PackFormModal;
